import { PageContainer } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  Box,
  Button,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import {
  type FC,
  type ReactNode,
  type SubmitEvent,
  type SyntheticEvent,
  useCallback,
  useState,
} from "react";
import { ForbiddenError } from "../../../errors/ForbiddenError";
import { api } from "../../../services/api";
import { getMicrosoftToken } from "../../../services/getMicrosoftToken";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      aria-labelledby={`developer-tab-${index}`}
      hidden={value !== index}
      id={`developer-tabpanel-${index}`}
      role="tabpanel"
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const ToolsTab: FC = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [purchaseRequisitionItems, setPurchaseRequisitionItems] = useState<
    string[]
  >([]);
  const [result, setResult] = useState<string>("");

  const handleApiCall = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const response = await api.post(
        "/developer_tools/close_purchase_requisition",
        {
          purchase_requisition_items: purchaseRequisitionItems.filter(
            (item) => item !== "",
          ),
        },
        {
          baseURL: "/",
        },
      );
      setResult(JSON.stringify(response.data));
      // biome-ignore lint/suspicious/noExplicitAny: Any is used to catch all error types
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to close purchase requisition items.",
        { variant: "error" },
      );
      setResult("");
    } finally {
      setPurchaseRequisitionItems([]);
      setIsSubmitting(false);
    }
  }, [purchaseRequisitionItems]);

  const handleSubmit = useCallback(
    async (e: SubmitEvent<HTMLFormElement>) => {
      e.preventDefault();

      setIsAuthenticating(true);
      const authenticated = await getMicrosoftToken("procurement");
      setIsAuthenticating(false);
      if (!authenticated) {
        enqueueSnackbar(
          "Microsoft authentication failed. Please try again or contact support if the issue persists.",
          {
            variant: "warning",
          },
        );
        return;
      }
      handleApiCall();
    },
    [handleApiCall],
  );

  return (
    <Paper sx={{ p: 4, textAlign: "center" }}>
      {/* <Typography variant="h6" color="text.secondary" gutterBottom>
      Developer Tools
    </Typography>
    <Typography variant="body1" color="text.secondary">
      Tools section coming soon...
    </Typography> */}
      <Typography
        gutterBottom
        sx={{
          color: "text.secondary",
        }}
        variant="h6"
      >
        Purchase Requisition Closer
      </Typography>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="body1"
      >
        This tool allows developers to close purchase requisition items via the
        Equinor API. Please use with caution.
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box sx={{ my: 2 }}>
          <TextField
            fullWidth
            multiline
            name="purchase_requisition_items"
            onChange={(e) =>
              setPurchaseRequisitionItems(
                e.target.value.split("\n").map((item) => item.trim()),
              )
            }
            placeholder="Enter purchase requisition items (one per line, e.g. 100484727100030)"
            rows={10}
            value={purchaseRequisitionItems.join("\n")}
          />
        </Box>
        <Button
          disabled={purchaseRequisitionItems.length === 0}
          loading={isAuthenticating || isSubmitting}
          type="submit"
          variant="contained"
        >
          Close Purchase Requisition Items
        </Button>
      </form>
      <Box sx={{ mt: 2 }}>{result}</Box>
    </Paper>
  );
};

const DevelopersPage: FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };

  return (
    <PageContainer title="Developer Resources">
      <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
        <Tabs
          aria-label="developer navigation tabs"
          onChange={handleTabChange}
          value={tabValue}
        >
          <Tab label="Tools" />
        </Tabs>
      </Box>

      <TabPanel index={0} value={tabValue}>
        <ToolsTab />
      </TabPanel>
    </PageContainer>
  );
};

export const Route = createFileRoute("/_private/developers/")({
  beforeLoad({ context }) {
    if (context.auth.user?.is_developer) {
      return;
    }

    throw new ForbiddenError();
  },
  component: DevelopersPage,
});
